// The one seam between the app approval and policy screens and the server.
// Routes belong to ART's registration and the owners' services (PUB for
// approvals and connection policy); none is invented here. Until a route is
// registered the screens show their unavailable state, never a guess.
// `registerAppsRoutes` is called by the root registration; tests install a
// scripted transport with `setAppsTransport`.

export type AppsRouteName =
  | 'approval.get'
  | 'approval.decide'
  | 'grant.get'
  | 'grant.decide'
  | 'business.get'
  | 'business.decide'
  | 'approval.receipt'
  | 'grant.receipt'
  | 'business.receipt'
  | 'commandDigest'
  | 'connectionPolicy.get'
  | 'connectionPolicy.set';

export interface AppsRoute {
  method: 'GET' | 'POST' | 'PUT';
  /** `{id}` is replaced with the encoded id the caller passes. */
  path: string;
}
export type AppsRouteTable = Partial<Record<AppsRouteName, AppsRoute>>;

export interface AppsResponse {
  status: number;
  body: unknown;
}
export interface AppsTransport {
  request(name: AppsRouteName, id: string, body?: unknown): Promise<AppsResponse>;
  /**
   * The canonical command digest of a decision, produced by the trusted
   * registered command mapping (C8 / the business decision digest), never by
   * the UI. Absent until ART registers it alongside the routes.
   */
  commandDigest?(command: Record<string, unknown>): Promise<string>;
}

/** Thrown when the route has not been registered: the screen says it is unavailable. */
export class AppsRouteUnregistered extends Error {
  constructor(name: string) {
    super(`apps route not registered: ${name}`);
    this.name = 'AppsRouteUnregistered';
  }
}

let routes: AppsRouteTable = {};
export function registerAppsRoutes(table: AppsRouteTable) {
  routes = { ...routes, ...table };
}
/**
 * The decision routes served by src/apps/approvals/routes.mjs. Kinds the server
 * does not back answer unavailable there; nothing here decides which exist.
 */
export function installAppsDecisionRoutes(prefix = '/api/v1/company/apps/decisions') {
  const table: AppsRouteTable = { commandDigest: { method: 'POST', path: `${prefix}/digest` } };
  for (const [kind, key] of [
    ['approval', 'approval'],
    ['grant', 'grant'],
    ['business', 'business'],
  ] as const) {
    table[`${key}.get`] = { method: 'GET', path: `${prefix}/${kind}/{id}` };
    table[`${key}.decide`] = { method: 'POST', path: `${prefix}/${kind}/{id}/decide` };
    table[`${key}.receipt`] = { method: 'POST', path: `${prefix}/${kind}/{id}/receipt` };
  }
  registerAppsRoutes(table);
}
export function resetAppsRoutesForTests() {
  routes = {};
  transport = fetchTransport;
}

/**
 * A fetch transport over ONE fetch. An action (digest, then decide, then a receipt
 * check) must keep the fetch it began with: the session guard replaces the page's
 * global fetch, and a later call must never follow a replacement guard.
 */
function createFetchTransport(send: () => typeof fetch): AppsTransport {
  const self: AppsTransport = {
    // The digest is the server's: the command goes out, the canonical digest comes back.
    async commandDigest(command) {
      const response = await self.request('commandDigest', String(command.id ?? ''), command);
      const digest = (response.body as { digest?: unknown } | null)?.digest;
      if (response.status !== 200 || typeof digest !== 'string')
        throw new Error('digest_unavailable');
      return digest;
    },
    async request(name, id, body) {
      const route = routes[name];
      if (!route) throw new AppsRouteUnregistered(name);
      const response = await send()(route.path.replace('{id}', encodeURIComponent(id)), {
        method: route.method,
        credentials: 'include',
        headers: body === undefined ? undefined : { 'content-type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      let parsed: unknown = null;
      try {
        parsed = await response.json();
      } catch {
        parsed = null;
      }
      return { status: response.status, body: parsed };
    },
  };
  return self;
}
const fetchTransport = createFetchTransport(() => fetch);

let transport: AppsTransport = fetchTransport;
export const setAppsTransport = (next: AppsTransport) => {
  transport = next;
};
export const appsTransport: AppsTransport = {
  request: (name, id, body) => transport.request(name, id, body),
  commandDigest: (command) =>
    transport.commandDigest
      ? transport.commandDigest(command)
      : Promise.reject(new AppsRouteUnregistered('commandDigest')),
};

/**
 * The transport for one mounted card: its calls use `bound` (the session-bound
 * fetch committed with the card) for the card's whole life. A scripted transport
 * installed with `setAppsTransport` (tests, fixtures) still wins.
 */
export function appsTransportBoundTo(bound: typeof fetch): AppsTransport {
  return transport === fetchTransport ? createFetchTransport(() => bound) : appsTransport;
}
